import * as React from "react";
import {useMemo, useState} from "react";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {IEngagementTabResponse} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {
    AREA_USAGE_DESCRIPTION,
    axisTickStyle,
    chartColors,
    gridProps,
    gridStroke,
    legendStyle,
    tooltipStyle,
} from "../shared/analytics.constants";
import {AreaTrendsChart} from "./AreaTrendsChart.component";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IAreaUsageSectionProps {
    areaUsage: IEngagementTabResponse["areaUsage"];
    allAreaUsage: IEngagementTabResponse["allAreaUsage"];
    areaTrends: IEngagementTabResponse["areaTrends"];
}

export const AreaUsageSection: React.FC<IAreaUsageSectionProps> = (props) => {
    const [areaUsageSort, setAreaUsageSort] = useState<{ col: string; dir: SortDir }>({col: "total", dir: "desc"});
    const [showAllAreas, setShowAllAreas] = useState<boolean>(false);
    const sortedAreaUsage = useMemo(
        () => applySort(props.allAreaUsage, areaUsageSort.col, areaUsageSort.dir),
        [props.allAreaUsage, areaUsageSort]);

    return (
        <section className="admin-analytics__section" data-testid={"area-usage"}>
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Site Area Usage</h2>
                <span className="admin-analytics__section-subtitle">
                    Top areas (excludes /home and /client-reporting)
                </span>
            </div>
            <p className="admin-analytics__section-desc">{AREA_USAGE_DESCRIPTION}</p>
            <div className="admin-analytics__chart">
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={props.areaUsage} margin={{bottom: 60}}>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="area" angle={-45} textAnchor="end" interval={0} height={80}
                               tick={axisTickStyle} axisLine={{stroke: gridStroke}}
                               tickLine={false}/>
                        <YAxis tick={axisTickStyle} axisLine={false} tickLine={false}/>
                        <Tooltip {...tooltipStyle} />
                        <Legend {...legendStyle} />
                        <Bar dataKey="internal" stackId="a" fill={chartColors.navy} name="Internal"/>
                        <Bar dataKey="external" stackId="a" fill={chartColors.tealLight} name="External"/>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {props.allAreaUsage.length > 0 && (
                <div>
                    <button className="admin-analytics__show-more-btn"
                            onClick={() => setShowAllAreas(!showAllAreas)}>
                        {showAllAreas ? "Hide full table" : `Show all ${props.allAreaUsage.length} pages`}
                    </button>
                    {showAllAreas && (
                        <table className="admin-analytics__table" style={{marginTop: 12}}>
                            <thead>
                            <tr>
                                {sortTh("area", "Area", areaUsageSort, setAreaUsageSort, undefined, "asc")}
                                {sortTh("internal", "Internal", areaUsageSort, setAreaUsageSort,
                                    "admin-analytics__cell--right")}
                                {sortTh("external", "External", areaUsageSort, setAreaUsageSort,
                                    "admin-analytics__cell--right")}
                                {sortTh("total", "Total", areaUsageSort, setAreaUsageSort,
                                    "admin-analytics__cell--right")}
                            </tr>
                            </thead>
                            <tbody>{sortedAreaUsage.map((row) => (
                                <tr key={row.area}>
                                    <td>{row.area}</td>
                                    <td className="admin-analytics__cell--right">
                                        {numberWithCommas(row.internal)}
                                    </td>
                                    <td className="admin-analytics__cell--right">
                                        {numberWithCommas(row.external)}
                                    </td>
                                    <td className="admin-analytics__cell--right">{numberWithCommas(row.total)}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
            )}
            <AreaTrendsChart areaTrends={props.areaTrends}/>
        </section>
    );
};
